import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useTimeAgoLocale, formatTimeAgo } from '../../hooks/useTimeAgo';
import { usePdfList } from '../../queries/pdfs';
import PdfSelectListItem from '../../components/qr/PdfSelectListItem';

/**
 * @description QR 스캔 성공 후 프레젠테이션 선택 페이지
 */
export default function QRSelectPage() {
  const [userId, setUserId] = useState<string | null>(null);
  const { data: pdfList = [], isLoading: loading, error: queryError } = usePdfList(userId);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timeAgoLocale = useTimeAgoLocale();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/login');
          return;
        }
        setUserId(user.id);
      } catch {
        setError(t('qr.qr_scan_error'));
      }
    };
    checkAuth();
  }, [navigate, t]);

  useEffect(() => {
    if (queryError) setError(t('qr.qr_scan_error'));
  }, [queryError, t]);

  /**
   * @description 프레젠테이션을 선택하여 리모트 뷰어로 이동한다.
   * @param {any} pdf - 선택된 PDF 파일 정보
   */
  const handleSelectPdf = (pdf: any) => {
    navigate(`/presentations/${pdf.id}/remote`);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* 헤더 */}
        <div className="mb-5">
          <h1 className="text-lg font-bold text-gray-900">{t('qr.select_presentation')}</h1>
          <p className="text-sm text-gray-400 mt-0.5">{t('qr.select_presentation_desc')}</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-xs flex items-center gap-2">
            <i className="ri-error-warning-line flex-shrink-0"></i>
            <span>{error}</span>
          </div>
        )}

        {/* 인증 완료 배지 */}
        <div className="flex items-center gap-1.5 text-xs text-green-600 mb-5">
          <i className="ri-checkbox-circle-fill"></i>
          <span>{t('qr.auth_complete')}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-500 border-t-transparent"></div>
          </div>
        ) : pdfList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <i className="ri-file-pdf-line text-4xl mb-2"></i>
            <p className="text-sm">{t('qr.no_presentations')}</p>
            <p className="text-xs mt-1">{t('qr.no_presentations_hint')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pdfList.map((pdf) => (
              <PdfSelectListItem
                key={pdf.id}
                thumbnailUrl={pdf.thumbnail_url}
                fileName={pdf.file_name}
                pageCount={pdf.page_count}
                metaText={formatTimeAgo(pdf.created_at, timeAgoLocale)}
                onClick={() => handleSelectPdf(pdf)}
              />
            ))}
          </div>
        )}

        <div className="mt-5">
          <Link
            to="/dashboard"
            className="w-full border border-gray-200 text-gray-500 py-2.5 rounded-lg text-sm hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            {t('qr.back_to_dashboard')}
          </Link>
        </div>
      </div>
    </div>
  );
}
